import { AxiosResponse } from 'axios';
import axiosInstance from '../../../axiosInstance';
import { setShowAlert } from '../../utilts/utilsSlice';
import { TOAST_TYPE } from '../../../utils/constantTypes';
import {
  CreateStaffContactPayload,
  DeleteStaffContactPayload,
  FetchStaffContactsPayload,
  StaffContactApiResponse,
  StaffContactListQuery,
  StaffContactPayload,
  UpdateStaffContactPayload,
} from './types';

type ApiDispatch = {
  dispatch: (action: ReturnType<typeof setShowAlert>) => void;
};

const authHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
});

const apiErrorMessage = (err: {
  response?: { data?: { msg?: string } };
  message?: string;
}) => err?.response?.data?.msg || err?.message || 'Something went wrong';

const withStaffContactsApi = async <T extends StaffContactApiResponse>(
  request: () => Promise<AxiosResponse<T>>,
  { dispatch }: ApiDispatch,
): Promise<T> => {
  try {
    const response = await request();
    return response.data;
  } catch (err) {
    const message = apiErrorMessage(err);
    dispatch(
      setShowAlert({
        show: true,
        message,
        type: TOAST_TYPE.ERROR,
      }),
    );
    throw new Error(message);
  }
};

export const getStaffContacts = (
  payload: FetchStaffContactsPayload,
  query: StaffContactListQuery,
  ctx: ApiDispatch,
) =>
  withStaffContactsApi(
    () =>
      axiosInstance.get('/staff-contacts', {
        headers: authHeaders(payload.accessToken),
        params: {
          offset: query.offset,
          limit: query.limit,
          ...(query.sortBy ? { sortBy: query.sortBy } : {}),
          ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
        },
      }),
    ctx,
  );

export const createStaffContact = (
  payload: CreateStaffContactPayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, name, type } = payload;
  return withStaffContactsApi(
    () =>
      axiosInstance.post(
        '/staff-contacts',
        { name, type },
        { headers: authHeaders(accessToken) },
      ),
    ctx,
  );
};

export const updateStaffContact = (
  payload: UpdateStaffContactPayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, id, name, type } = payload;
  return withStaffContactsApi(
    () =>
      axiosInstance.patch(
        `/staff-contacts/${id}`,
        { name, type },
        { headers: authHeaders(accessToken) },
      ),
    ctx,
  );
};

export const deleteStaffContact = (
  payload: DeleteStaffContactPayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, id } = payload;
  return withStaffContactsApi(
    () =>
      axiosInstance.delete(`/staff-contacts/${id}`, {
        headers: authHeaders(accessToken),
      }),
    ctx,
  );
};
